import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import type { GenerateReportStatus, MarketingReportTimeRange } from '../types';

const STORAGE_KEY = 'marketing-report-jobs';

const memoryStorage = (() => {
  const store: Record<string, string> = {};

  return {
    getItem: (name: string) => store[name] ?? null,
    setItem: (name: string, value: string) => {
      store[name] = value;
    },
    removeItem: (name: string) => {
      delete store[name];
    },
  };
})();

export interface ReportJobRecord {
  jobId: string;
  restaurantId: string;
  status: GenerateReportStatus;
  reportType: string | null;
  downloadUrl: string | null;
  expiresAt: string | null;
  locale: string;
  startedAt: string | null;
  error: string | null;
  timeRange?: MarketingReportTimeRange;
  templateId?: string | null;
  updatedAt: number;
}

interface ReportJobState {
  jobs: Record<string, ReportJobRecord>;
}

type UpsertableReportJobRecord = Omit<
  ReportJobRecord,
  'restaurantId' | 'updatedAt'
> & {
  restaurantId: number | string;
};

interface ReportJobStore extends ReportJobState {
  upsertJob: (entry: UpsertableReportJobRecord) => void;
  updateJob: (
    jobId: string,
    entry: Partial<
      Omit<ReportJobRecord, 'jobId' | 'restaurantId' | 'updatedAt'>
    >,
  ) => void;
  removeJob: (jobId: string) => void;
  clear: () => void;
}

const createInitialState = (): ReportJobState => ({
  jobs: {},
});

const toRestaurantKey = (restaurantId: number | string): string =>
  typeof restaurantId === 'string' ? restaurantId : String(restaurantId);

export const useReportJobStore = create<ReportJobStore>()(
  persist(
    (set) => ({
      ...createInitialState(),

      upsertJob: (entry) => {
        set((state) => ({
          jobs: {
            ...state.jobs,
            [entry.jobId]: {
              ...entry,
              restaurantId: toRestaurantKey(entry.restaurantId),
              updatedAt: Date.now(),
            },
          },
        }));
      },

      updateJob: (jobId, entry) => {
        set((state) => {
          const existing = state.jobs[jobId];
          if (!existing) {
            return state;
          }

          return {
            jobs: {
              ...state.jobs,
              [jobId]: {
                ...existing,
                ...entry,
                restaurantId: existing.restaurantId,
                updatedAt: Date.now(),
              },
            },
          };
        });
      },

      removeJob: (jobId) => {
        set((state) => {
          if (!state.jobs[jobId]) {
            return state;
          }

          const nextJobs = { ...state.jobs };
          delete nextJobs[jobId];

          return {
            jobs: nextJobs,
          };
        });
      },

      clear: () => {
        set(() => createInitialState());
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => {
        if (typeof window === 'undefined' || !window.localStorage) {
          return memoryStorage;
        }
        return window.localStorage;
      }),
      partialize: (state) => ({
        jobs: state.jobs,
      }),
    },
  ),
);

export const isReportJobActive = (status: GenerateReportStatus): boolean =>
  status === 'pending' || status === 'processing' || status === 'submitting';

export const isReportJobTerminal = (status: GenerateReportStatus): boolean =>
  status === 'ready' || status === 'failed';

export const findLatestJobForRestaurant = (
  jobs: Record<string, ReportJobRecord>,
  restaurantId: number | string | null | undefined,
): ReportJobRecord | null => {
  if (restaurantId === null || restaurantId === undefined) {
    return null;
  }

  const target = toRestaurantKey(restaurantId);
  let latest: ReportJobRecord | null = null;

  Object.values(jobs).forEach((job) => {
    if (job.restaurantId !== target) {
      return;
    }

    if (!latest || job.updatedAt > latest.updatedAt) {
      latest = job;
    }
  });

  return latest;
};

export const selectReportJobs = (state: ReportJobStore) => state.jobs;
export const selectRemoveReportJob = (state: ReportJobStore) => state.removeJob;
export const selectUpsertReportJob = (state: ReportJobStore) => state.upsertJob;
export const selectUpdateReportJob = (state: ReportJobStore) => state.updateJob;
export const selectClearReportJobs = (state: ReportJobStore) => state.clear;
